import { useState, useRef, useEffect, useMemo } from "react";

import moment from "moment-timezone";

// @mui material components
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { Bar, Chart } from "react-chartjs-2";

//Backend access
import { loadToken, getCameraHistory } from 'api/bks';
import PropTypes from "prop-types";

import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import CameraImage from "nextus/CameraImage";
import { PacmanLoader } from "react-spinners";
import { Card, Slider, ThemeProvider } from "@mui/material";

import ClearwayOverlay from '../../../../nextus-assets/images/clearway_overlay.png';
import { createTheme } from '@mui/material/styles';
import SoftTypography from "components/SoftTypography";

import theme from "assets/theme";
const { palette, boxShadows, functions, transitions, breakpoints, borders } = theme;


const sliderTheme = createTheme({
    palette: {
        primary: {
            light: '#757ce8',
            main: '#3f50b5',
            dark: '#002884',
            contrastText: '#fff',
        },
        secondary: {
            light: '#ff7961',
            main: '#f44336',
            dark: '#ba000d',
            contrastText: '#000',
        },
    },
});

// Images
function LiveviewFeed({ uuid, currentTime, setCurrentTime, startTime, setStartTime, endTime, setEndTime, liveviewData, isLoading, current_liveview_url }) {
    const [selectedImage, setSelectedImage] = useState({
        index: liveviewData[Number(liveviewData.length) - 1] ? Number(liveviewData.length) - 1 : -1,
        image: liveviewData[Number(liveviewData.length) - 1] ? liveviewData[Number(liveviewData.length) - 1].image_hash : null,
        time_displayed: liveviewData[Number(liveviewData.length) - 1] ? liveviewData[Number(liveviewData.length) - 1].time_displayed : moment().subtract(1, 'day').unix(),
        time_changed: liveviewData[Number(liveviewData.length) - 1] ? liveviewData[Number(liveviewData.length) - 1].time_changed : moment().unix()
    });

    loadToken(useAuth0());

    function nextImage(currentTime, key_used = false) {
        var now_time = false;
        var new_image = {
            index: selectedImage.index,
            image: selectedImage.image_hash,
            time_displayed: selectedImage.time_displayed,
            time_changed: selectedImage.time_changed,
        }

        if (key_used) {
            new_image.index++;
        }
        else {
            for (var image_index in liveviewData) {
                if (currentTime >= liveviewData[image_index].time_displayed && currentTime <= liveviewData[image_index].time_changed) {
                    new_image.index = Number(image_index);
                    break;
                }
            }
        }

        if (new_image.index <= -1 || (liveviewData.length && new_image.index >= liveviewData.length)) {
            if (new_image.index >= liveviewData.length) {
                var now_time = true;
            }
            new_image.index = liveviewData.length - 1;
        }

        if (typeof liveviewData != 'undefined' && typeof liveviewData[new_image.index] != 'undefined') {
            new_image.image = liveviewData[new_image.index].image_hash;
            new_image.time_displayed = liveviewData[new_image.index].time_displayed;
            new_image.time_changed = liveviewData[new_image.index].time_changed;
        }

        if (key_used) {
            if (now_time) {
                setCurrentTime(endTime);
            }
            else {
                setCurrentTime(Number(new_image.time_displayed) + 1);
            }
        }

        setSelectedImage(new_image);
    }

    function previousImage(currentTime, key_used = false) {
        var start_time = false;
        var new_image = {
            index: selectedImage.index,
            image: selectedImage.image_hash,
            time_displayed: selectedImage.time_displayed,
            time_changed: selectedImage.time_changed,
        };

        if (key_used) {
            new_image.index--;
        }
        else {
            for (var image_index in liveviewData) {
                if (currentTime >= liveviewData[image_index].time_displayed && currentTime <= liveviewData[image_index].time_changed) {
                    new_image.index = Number(image_index);
                    break;
                }
            }
        }

        if (new_image.index <= -1 || new_image.index >= liveviewData.length) {
            if (selectedImage.index == 0 && new_image.index <= -1) {
                new_image.index = 0;
            }
            else {
                if(new_image.index >= liveviewData.length){
                    var start_time = true;
                }
                new_image.index = liveviewData.length - 1;
            }
        }

        if (typeof liveviewData != 'undefined' && typeof liveviewData[new_image.index] != 'undefined') {
            new_image.image = liveviewData[new_image.index].image_hash;
            new_image.time_displayed = liveviewData[new_image.index].time_displayed;
            new_image.time_changed = liveviewData[new_image.index].time_changed;
        }

        if (key_used) {
            if(start_time){
                setCurrentTime(startTime);
            }
            else {
                setCurrentTime(Number(new_image.time_displayed) + 1);
            }
        }

        setSelectedImage(new_image);
    }

    function handleKeyDown(e) {
        // arrow up/down button should select next/previous list element
        if (e.keyCode === 37) {
            previousImage(null, true);
        } else if (e.keyCode === 39) {
            nextImage(null, true);
        }
    }

    function valueLabelFormat(value) {
        return `${moment(value * 1000).format('hh:mmA, DD/MM/YYYY')}`;
    }

    const handleChange = (e, newValue) => {
        if (newValue < selectedImage.time_displayed) {
            previousImage(newValue);
        }
        else if (newValue > selectedImage.time_changed) {
            nextImage(newValue);
        }

        setCurrentTime(newValue);
    }

    const dateSlider = (
        <Card sx={{ border: '1px solid #ccc' }}>
            {window.innerWidth > breakpoints.values.x5l ? (
                <SoftBox px={2} py={5} textAlign="center">
                    <Grid container>
                        <Grid item xxs={12} x5l={3} x6l={2.5}>
                            <SoftBox display="flex" justifyContent="center" alignItems="center">
                                <SoftBox>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        {isLoading ? (
                                            <DatePicker format="DD/MM/YYYY" value={moment(startTime * 1000)} maxDate={moment(endTime * 1000).clone().subtract(1, 'day')} onChange={(new_value) => { setStartTime(new_value.unix()) }} disabled />
                                        ) : (
                                            <DatePicker format="DD/MM/YYYY" value={moment(startTime * 1000)} maxDate={moment(endTime * 1000).clone().subtract(1, 'day')} onChange={(new_value) => { setStartTime(new_value.unix()) }} />
                                        )}

                                    </LocalizationProvider>
                                </SoftBox>
                            </SoftBox>
                        </Grid>
                        <Grid item xxs={12} x5l={6} x6l={7}>
                            <SoftBox display="flex" justifyContent="center" alignItems="center" sx={{ width: "100%" }}>
                                <ThemeProvider theme={sliderTheme}>
                                    {isLoading ? (
                                        <Slider color="primary" value={currentTime} getAriaValueText={valueLabelFormat} valueLabelFormat={valueLabelFormat} valueLabelDisplay="auto" onChange={handleChange} min={startTime} max={endTime} disabled />
                                    ) : (
                                        <Slider color="primary" value={currentTime} getAriaValueText={valueLabelFormat} valueLabelFormat={valueLabelFormat} valueLabelDisplay="auto" onChange={handleChange} min={startTime} max={endTime} />
                                    )}
                                </ThemeProvider>
                            </SoftBox>
                        </Grid>
                        <Grid item xxs={12} x5l={3} x6l={2.5}>
                            <SoftBox display="flex" justifyContent="center" alignItems="center">
                                <SoftBox>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        {isLoading ? (
                                            <DatePicker format="DD/MM/YYYY" value={moment(endTime * 1000)} minDate={moment(startTime * 1000).clone().add(1, 'day')} onChange={(new_value) => { setEndTime(new_value.unix()) }} disabled />
                                        ) : (
                                            <DatePicker format="DD/MM/YYYY" value={moment(endTime * 1000)} minDate={moment(startTime * 1000).clone().add(1, 'day')} onChange={(new_value) => { setEndTime(new_value.unix()) }} />
                                        )}
                                    </LocalizationProvider>
                                </SoftBox>
                            </SoftBox>
                        </Grid>
                    </Grid>
                </SoftBox>
            ) : (
                <SoftBox px={2} py={5} textAlign="center">
                    <Grid container>
                        <Grid item xxs={12} sm={6}>
                            <SoftBox display="flex" justifyContent={{ xxs: "center", sm: "flex-start" }} alignItems="center">
                                <SoftBox>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        {isLoading ? (
                                            <DatePicker format="DD/MM/YYYY" value={moment(startTime * 1000)} maxDate={moment(endTime * 1000).clone().subtract(1, 'day')} onChange={(new_value) => { setStartTime(new_value.unix()) }} disabled />
                                        ) : (
                                            <DatePicker format="DD/MM/YYYY" value={moment(startTime * 1000)} maxDate={moment(endTime * 1000).clone().subtract(1, 'day')} onChange={(new_value) => { setStartTime(new_value.unix()) }} />
                                        )}

                                    </LocalizationProvider>
                                </SoftBox>
                            </SoftBox>
                        </Grid>
                        <Grid item xxs={12} sm={6}>
                            <SoftBox display="flex" justifyContent={{ xxs: "center", sm: "flex-end" }} alignItems="center">
                                <SoftBox>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        {isLoading ? (
                                            <DatePicker format="DD/MM/YYYY" value={moment(endTime * 1000)} minDate={moment(startTime * 1000).clone().add(1, 'day')} onChange={(new_value) => { setEndTime(new_value.unix()) }} disabled />
                                        ) : (
                                            <DatePicker format="DD/MM/YYYY" value={moment(endTime * 1000)} minDate={moment(startTime * 1000).clone().add(1, 'day')} onChange={(new_value) => { setEndTime(new_value.unix()) }} />
                                        )}
                                    </LocalizationProvider>
                                </SoftBox>
                            </SoftBox>
                        </Grid>
                        {/* <Grid item xxs={1} /> */}
                        <Grid item xxs={12}>
                            <SoftBox display="flex" pt={1} justifyContent="center" alignItems="center" sx={{ width: "100%" }}>
                                <ThemeProvider theme={sliderTheme}>
                                    {isLoading ? (
                                        <Slider color="primary" value={currentTime} getAriaValueText={valueLabelFormat} valueLabelFormat={valueLabelFormat} valueLabelDisplay="auto" onChange={handleChange} min={startTime} max={endTime} disabled />
                                    ) : (
                                        <Slider color="primary" value={currentTime} getAriaValueText={valueLabelFormat} valueLabelFormat={valueLabelFormat} valueLabelDisplay="auto" onChange={handleChange} min={startTime} max={endTime} />
                                    )}
                                </ThemeProvider>
                            </SoftBox>
                        </Grid>
                    </Grid>
                </SoftBox>
            )}
        </Card>
    );

    var liveview_max_width = '1250px';
    if (window.innerWidth < breakpoints.values.lg) {
        liveview_max_width = '475px';
    }
    else if (window.innerWidth < breakpoints.values.xl) {
        liveview_max_width = '540px';
    }
    else if (window.innerWidth < breakpoints.values.xxl) {
        liveview_max_width = '600px';
    }
    else if (window.innerWidth < breakpoints.values.x3l) {
        liveview_max_width = '700px';
    }

    const loading = (<>
        <Grid item xxs={12}>
            {dateSlider}
        </Grid>
        <Grid item xxs={12}>
            <SoftBox sx={{ width: "100%", display: 'flex', justifyContent: 'center' }}>
                <SoftBox maxWidth={liveview_max_width} sx={{ width: "100%" }}>
                    <SoftBox width="100%" height={{ xxs: "18vmax", sm: "24vmax", md: '24vmax' }} sx={{ marginLeft: '2%', marginTop: '3vmin' }}>
                        <SoftBox width="100%" height="100%" position="relative" borderRadius="lg">
                            <SoftBox
                                component="img"
                                src={current_liveview_url}
                                position="absolute"
                                width="57%"
                                borderRadius="lg"
                                sx={{ marginLeft: "34.8%", marginTop: "5%" }}
                            />
                            <SoftBox
                                component="img"
                                src={ClearwayOverlay}
                                position="absolute"
                                top={0}
                                left={0}
                                width="96%"
                                borderRadius="lg"
                            />
                        </SoftBox>
                    </SoftBox>
                </SoftBox>
            </SoftBox>
        </Grid>
    </>);

    const rendered = (<>
        <Grid item xxs={12}>
            {dateSlider}
        </Grid>
        <Grid item xxs={12}>
            <SoftBox sx={{ width: "100%", display: 'flex', justifyContent: 'center' }}>
                <SoftBox maxWidth={liveview_max_width} sx={{ width: "100%" }}>
                    <SoftBox width="100%" height={{ xxs: "18vmax", sm: "24vmax", md: '24vmax' }} sx={{ marginLeft: '2%', marginTop: '3vmin' }}>
                        <SoftBox width="100%" height="100%" position="relative" borderRadius="lg">
                            {selectedImage.image ? (
                                <SoftBox
                                    component="img"
                                    src={selectedImage.image}
                                    position="absolute"
                                    width="57%"
                                    borderRadius="lg"
                                    sx={{ marginLeft: "34.8%", marginTop: "5%" }}
                                />
                            ) : (
                                <SoftBox
                                    component="img"
                                    src={current_liveview_url}
                                    position="absolute"
                                    width="57%"
                                    borderRadius="lg"
                                    sx={{ marginLeft: "34.8%", marginTop: "5%" }}
                                />
                            )}
                            <SoftBox
                                component="img"
                                src={ClearwayOverlay}
                                position="absolute"
                                top={0}
                                left={0}
                                width="96%"
                                borderRadius="lg"
                            />
                        </SoftBox>
                    </SoftBox>
                </SoftBox>
            </SoftBox>
        </Grid>
    </>);

    return (
        <Card sx={{height: window.innerWidth < breakpoints.values.xxl ? '100%' : 'unset'}}>
            <SoftBox p={2} mb={{xxs: 2, lg: 0}}>
                <Grid container sx={{ maxHeight: '850px' }} onKeyDown={handleKeyDown}>
                    {isLoading && loading}
                    {!isLoading && rendered}
                </Grid>
            </SoftBox>
        </Card>
    );
}

// Typechecking props for the LiveviewFeed
LiveviewFeed.propTypes = {
    uuid: PropTypes.string,
    currentTime: PropTypes.number,
    setCurrentTime: PropTypes.func,
    startTime: PropTypes.number,
    setStartTime: PropTypes.func,
    endTime: PropTypes.number,
    setEndTime: PropTypes.func,
    liveviewData: PropTypes.array,
    isLoading: PropTypes.bool,
    current_liveview_url: PropTypes.string,
};


export default LiveviewFeed;
